import React from 'react';
import compact from 'lodash/compact';
import { Typography, Button } from 'seagull';
import { Calendar, DollarCircle, PaymentMethod, PaymentTerms } from 'seagull/icons';
import { netTermsToString, numberAndUnit } from "../../../infra/utils";
import { useStore } from "../../../store/store-context";
import { StepPage } from "../../components/StepPage";
import { useOnMount } from "../../../hooks/useOnMount";
import { selectors } from "../../../store/state-selectors";
import { useResponsive } from "../../contexts";
import { useCobStorage } from "../CobStorageContext";
import { MobileSign } from "./MobileSign";
import { serviceModel } from "../../../models";
import { Intent } from "../../../infra/access-control";
import { relationshipModel } from "../../../models";
import { COBStep, getStepName } from "../ClientOnboarding.utils";
import { ErrorText } from "../../../constants";
import styles from "./OneLastStep.module.scss";

function paymentMethodToString(type, last4) {
  switch (type) {
    case 'bankAccount':
      return "Your Bank account ".concat(last4 ? "(".concat(last4, ") ") : '', "is properly set up!");

    case 'creditCard':
      return "Your credit card ".concat(last4 ? "that ends with \"".concat(last4, "\" ") : '', " is properly set up!");

    case 'microDeposit':
      return "Payment method approval is still pending your verification.";

    default:
      return '';
  }
}

export default function OneLastStep(props) {
  const {
    relationship,
    selectedPayment,
    selectedBundleID,
    isPreview,
    isMemberClient,
    COB_IMPROVEMENTS,
    approveAgreementStatus,
    onBack,
    approveAgreement,
    dismissPreviewModal
  } = props;
  const worksAt = useStore(selectors.user.business);
  const {
    getFromStorage
  } = useCobStorage(relationship);
  const {
    isDesktop
  } = useResponsive();
  const clientBusiness = relationship.isSample || isPreview ? relationship.customer.name : worksAt === null || worksAt === void 0 ? void 0 : worksAt.name;

  const services = (() => {
    const selectedOptionalServices = getFromStorage('selected_optional_services') || [];
    return relationshipModel.actualServices(relationship, selectedBundleID).filter(s => serviceModel.isMandatory(s.participation) || selectedOptionalServices.includes(s.id));
  })();

  useOnMount(() => {
    if (!COB_IMPROVEMENTS && (isMemberClient || relationship.requirePaymentMethod && !selectedPayment && !relationship.isSample && !isPreview)) {
      onBack();
    }
  });
  return /*#__PURE__*/React.createElement(StepPage, {
    header: getStepName(COBStep.summary, relationship, COB_IMPROVEMENTS),
    showLearnMore: true,
    relationship: relationship,
    errorTestid: "step9-error-message",
    errorMessage: ErrorText.UnexpectedErrorTryAgain,
    showError: approveAgreementStatus === 'error',
    primaryBtn: isPreview && dismissPreviewModal ? /*#__PURE__*/React.createElement(Button, {
      onClick: dismissPreviewModal,
      "data-testid": "preview-dismiss-modal"
    }, "Close preview") : isDesktop ? /*#__PURE__*/React.createElement(Button, {
      intent: Intent.ApproveProposal,
      disabled: approveAgreementStatus === 'loading' || isPreview,
      onClick: () => approveAgreement(),
      "data-testid": (relationship.isSample ? 'test-agreement-' : '') + 'step9-next-btn'
    }, relationship.isSample ? 'Approve test agreement' : 'Approve agreement') : /*#__PURE__*/React.createElement(MobileSign, {
      intent: Intent.ApproveProposal,
      relationship: relationship,
      onSign: () => approveAgreement()
    }),
    backBtnTestid: (relationship.isSample ? 'test-agreement-' : '') + 'step9-back-btn',
    testid: "one-last-step",
    onBackBtnClick: onBack
  }, /*#__PURE__*/React.createElement(Typography, null, "Digitally sign to approve the agreement."), !COB_IMPROVEMENTS && /*#__PURE__*/React.createElement("div", {
    className: styles.summary
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    skin: "secondary"
  }, "Agreement summary for ", clientBusiness), compact([{
    text: numberAndUnit(services.length, 'service'),
    testid: 'step9-services-count',
    icon: /*#__PURE__*/React.createElement(PaymentTerms, null)
  }, (!!selectedPayment || relationship.isSample || isPreview) && {
    text: "Payment terms: ".concat(netTermsToString(relationship.netTerms)),
    icon: /*#__PURE__*/React.createElement(DollarCircle, null)
  }, {
    text: "Effective date: ".concat(relationshipModel.formatEffectiveDate(relationship)),
    icon: /*#__PURE__*/React.createElement(Calendar, null)
  }, (!!selectedPayment || relationship.isSample) && {
    text: selectedPayment ? "Payment method: ".concat(paymentMethodToString(selectedPayment.type, selectedPayment.last4)) : 'Payment method will appear based on client selection',
    icon: /*#__PURE__*/React.createElement(PaymentMethod, null),
    testid: 'step9-payment-method'
  }]).map((_ref, idx) => {
    let {
      text,
      icon,
      testid
    } = _ref;
    return /*#__PURE__*/React.createElement("div", {
      key: idx,
      className: styles.summaryItem,
      "data-testid": testid
    }, icon, /*#__PURE__*/React.createElement(Typography, null, text));
  })));
}